/**
 * QuestionsAudit
 * Knowledge Hub tab for preparing questions from a datasource's documents,
 * letting stakeholders review/answer them, and viewing the full audit history.
 */
import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  Sparkles,
  Plus,
  Trash2,
  Pencil,
  Check,
  X,
  MessageSquare,
  History,
  FileText,
  RefreshCw,
  CheckCircle2,
  XCircle,
  Clock,
  Send,
  Download,
  Upload,
  Gauge,
  DatabaseZap,
  Users,
} from 'lucide-react';
import { Button, Dropdown } from '../../../components/ui';
import api, {
  Datasource,
  KnowledgeQuestion,
  KnowledgeAuditEvent,
  QuestionStatus,
  KnowledgeAuditAction,
} from '../../../services/api';
import styles from './QuestionsAudit.module.css';

interface QuestionsAuditProps {
  datasources: Datasource[];
}

const STATUS_LABELS: Record<QuestionStatus, string> = {
  draft: 'Draft',
  pending_review: 'Pending Review',
  approved: 'Approved',
  rejected: 'Rejected',
  answered: 'Answered',
};

/** Standard SDLC stakeholder roles offered as quick picks for preparation. */
const STAKEHOLDER_PRESETS = [
  'Developers',
  'QA',
  'Product Owner',
  'Project Manager',
  'Business Analyst',
];
const GENERAL_GROUP = 'General';

const AUDIT_LABELS: Record<KnowledgeAuditAction, string> = {
  document_added: 'Document added',
  questions_generated: 'Questions prepared',
  question_created: 'Question added',
  question_edited: 'Question edited',
  question_deleted: 'Question deleted',
  question_answered: 'Question answered',
  question_reviewed: 'Question reviewed',
  questions_exported: 'Questions exported',
  answers_uploaded: 'Answers uploaded',
  document_scored: 'Document scored',
  added_to_rag: 'Added to RAG',
};

const auditIcon = (action: KnowledgeAuditAction): React.ReactNode => {
  switch (action) {
    case 'document_added':
      return <FileText size={14} />;
    case 'questions_generated':
      return <Sparkles size={14} />;
    case 'question_created':
      return <Plus size={14} />;
    case 'question_edited':
      return <Pencil size={14} />;
    case 'question_deleted':
      return <Trash2 size={14} />;
    case 'question_answered':
      return <MessageSquare size={14} />;
    case 'question_reviewed':
      return <CheckCircle2 size={14} />;
    case 'questions_exported':
      return <Download size={14} />;
    case 'answers_uploaded':
      return <Upload size={14} />;
    case 'document_scored':
      return <Gauge size={14} />;
    case 'added_to_rag':
      return <DatabaseZap size={14} />;
    default:
      return <Clock size={14} />;
  }
};

export const QuestionsAudit: React.FC<QuestionsAuditProps> = ({ datasources }) => {
  const [selectedDatasourceId, setSelectedDatasourceId] = useState<string>('');
  const [questions, setQuestions] = useState<KnowledgeQuestion[]>([]);
  const [audit, setAudit] = useState<KnowledgeAuditEvent[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [newQuestionText, setNewQuestionText] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editText, setEditText] = useState('');
  const [answeringId, setAnsweringId] = useState<string | null>(null);
  const [answerText, setAnswerText] = useState('');

  const [stakeholders, setStakeholders] = useState('');
  const [scoreResult, setScoreResult] = useState<
    { overallScore: number; answeredCount: number; totalCount: number; summary: string } | null
  >(null);
  const [isExporting, setIsExporting] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [isScoring, setIsScoring] = useState(false);
  const [isAddingRag, setIsAddingRag] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!selectedDatasourceId && datasources.length > 0) {
      setSelectedDatasourceId(datasources[0].id);
    }
  }, [datasources, selectedDatasourceId]);

  const loadData = useCallback(async (datasourceId: string) => {
    if (!datasourceId) return;
    setIsLoading(true);
    setError(null);
    try {
      const [qRes, aRes] = await Promise.all([
        api.listKnowledgeQuestions(datasourceId),
        api.listKnowledgeAudit(datasourceId),
      ]);
      if (qRes.success) setQuestions(qRes.data || []);
      if (aRes.success) setAudit(aRes.data || []);
    } catch (err) {
      console.error('Failed to load questions/audit', err);
      setError('Failed to load questions and audit history.');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (selectedDatasourceId) loadData(selectedDatasourceId);
  }, [selectedDatasourceId, loadData]);

  const refresh = () => loadData(selectedDatasourceId);

  const handleGenerate = async () => {
    if (!selectedDatasourceId) return;
    setIsGenerating(true);
    setError(null);
    try {
      const stakeholderList = stakeholders
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean);
      const res = await api.generateKnowledgeQuestions(selectedDatasourceId, {
        count: stakeholderList.length > 0 ? 4 : 8,
        stakeholders: stakeholderList.length > 0 ? stakeholderList : undefined,
      });
      if (res.success) {
        await refresh();
      } else {
        setError('Question preparation failed.');
      }
    } catch (err) {
      console.error('Generate failed', err);
      setError('Question preparation failed. Ensure the datasource has content.');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleExport = async (format: 'csv' | 'json') => {
    if (!selectedDatasourceId) return;
    setIsExporting(true);
    setError(null);
    try {
      await api.exportKnowledgeQuestions(selectedDatasourceId, format);
      await refresh();
    } catch (err) {
      console.error('Export failed', err);
      setError('Export failed.');
    } finally {
      setIsExporting(false);
    }
  };

  const handleImportFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !selectedDatasourceId) return;
    setIsImporting(true);
    setError(null);
    setNotice(null);
    try {
      const res = await api.importKnowledgeAnswers(selectedDatasourceId, file);
      if (res.success) {
        setNotice(
          `Answers uploaded: ${res.data.updated} matched, ${res.data.skipped} skipped (of ${res.data.total}).`
        );
        await refresh();
      } else {
        setError('Answer upload failed.');
      }
    } catch (err) {
      console.error('Import failed', err);
      setError('Answer upload failed. Use an exported CSV/JSON file.');
    } finally {
      setIsImporting(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleScore = async () => {
    if (!selectedDatasourceId) return;
    setIsScoring(true);
    setError(null);
    try {
      const res = await api.scoreKnowledgeDocument(selectedDatasourceId);
      if (res.success) {
        setScoreResult(res.data);
        await refresh();
      } else {
        setError('Scoring failed.');
      }
    } catch (err) {
      console.error('Score failed', err);
      setError('Scoring failed.');
    } finally {
      setIsScoring(false);
    }
  };

  const handleAddToRag = async () => {
    if (!selectedDatasourceId) return;
    setIsAddingRag(true);
    setError(null);
    setNotice(null);
    try {
      const res = await api.addKnowledgeToRag(selectedDatasourceId);
      if (res.success) {
        setNotice(`Added ${res.data.ingested} Q&A pair(s) to RAG (${res.data.chunks} chunks).`);
        await refresh();
      } else {
        setError('Add to RAG failed.');
      }
    } catch (err) {
      console.error('Add to RAG failed', err);
      setError('Add to RAG failed. Answer & approve questions and link the datasource to an assistant first.');
    } finally {
      setIsAddingRag(false);
    }
  };

  const handleAddManual = async () => {
    const text = newQuestionText.trim();
    if (!text || !selectedDatasourceId) return;
    try {
      await api.createKnowledgeQuestion(selectedDatasourceId, text);
      setNewQuestionText('');
      await refresh();
    } catch (err) {
      console.error('Add question failed', err);
    }
  };

  const handleSaveEdit = async (questionId: string) => {
    const text = editText.trim();
    if (!text) return;
    try {
      await api.updateKnowledgeQuestion(selectedDatasourceId, questionId, text);
      setEditingId(null);
      setEditText('');
      await refresh();
    } catch (err) {
      console.error('Edit failed', err);
    }
  };

  const handleDelete = async (questionId: string) => {
    if (!window.confirm('Delete this question? The action is recorded in the audit history.')) return;
    try {
      await api.deleteKnowledgeQuestion(selectedDatasourceId, questionId);
      await refresh();
    } catch (err) {
      console.error('Delete failed', err);
    }
  };

  const handleReview = async (questionId: string, decision: 'approved' | 'rejected') => {
    try {
      await api.reviewKnowledgeQuestion(selectedDatasourceId, questionId, decision);
      await refresh();
    } catch (err) {
      console.error('Review failed', err);
    }
  };

  const handleSaveAnswer = async (questionId: string) => {
    const text = answerText.trim();
    if (!text) return;
    try {
      await api.answerKnowledgeQuestion(selectedDatasourceId, questionId, text);
      setAnsweringId(null);
      setAnswerText('');
      await refresh();
    } catch (err) {
      console.error('Answer failed', err);
    }
  };

  const statusClass = (status: QuestionStatus) => {
    switch (status) {
      case 'approved':
        return styles.statusApproved;
      case 'rejected':
        return styles.statusRejected;
      case 'answered':
        return styles.statusAnswered;
      case 'pending_review':
        return styles.statusPending;
      default:
        return styles.statusDraft;
    }
  };

  const selectedStakeholders = useMemo(
    () => stakeholders.split(',').map((s) => s.trim()).filter(Boolean),
    [stakeholders]
  );

  const toggleStakeholder = (role: string) => {
    const set = new Set(selectedStakeholders);
    if (set.has(role)) set.delete(role);
    else set.add(role);
    setStakeholders(Array.from(set).join(', '));
  };

  // Group questions by the stakeholder they were prepared for, in a stable order:
  // known SDLC roles first, then any custom roles, then unassigned ("General").
  const questionGroups = useMemo(() => {
    const map = new Map<string, KnowledgeQuestion[]>();
    for (const q of questions) {
      const key = (q.stakeholder && q.stakeholder.trim()) || GENERAL_GROUP;
      if (!map.has(key)) map.set(key, []);
      (map.get(key) as KnowledgeQuestion[]).push(q);
    }
    const preset = STAKEHOLDER_PRESETS.filter((k) => map.has(k));
    const custom = Array.from(map.keys())
      .filter((k) => k !== GENERAL_GROUP && !STAKEHOLDER_PRESETS.includes(k))
      .sort();
    const ordered = [...preset, ...custom];
    if (map.has(GENERAL_GROUP)) ordered.push(GENERAL_GROUP);
    return ordered.map((k) => ({ stakeholder: k, items: map.get(k) as KnowledgeQuestion[] }));
  }, [questions]);

  const groupStats = (items: KnowledgeQuestion[]) => {
    const scored = items.filter((q) => typeof q.answerScore === 'number');
    const answered = items.filter((q) => q.answer && q.answer.trim());
    const avg = scored.length
      ? Math.round(scored.reduce((a, q) => a + (q.answerScore || 0), 0) / scored.length)
      : null;
    return { total: items.length, answered: answered.length, avg };
  };

  const qualityAvg = useMemo(() => {
    const scored = questions.filter((q) => typeof q.answerScore === 'number');
    return scored.length
      ? Math.round(scored.reduce((a, q) => a + (q.answerScore || 0), 0) / scored.length)
      : 0;
  }, [questions]);

  const scoreBreakdown = questionGroups.map((g) => ({
    stakeholder: g.stakeholder,
    ...groupStats(g.items),
  }));

  const scoreTone = (v: number): 'good' | 'mid' | 'low' =>
    v >= 60 ? 'good' : v >= 30 ? 'mid' : 'low';

  if (datasources.length === 0) {
    return (
      <div className={styles.emptyState}>
        <FileText size={32} />
        <p>Add a datasource first, then prepare questions from its documents.</p>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.toolbar}>
        <div className={styles.dsSelect}>
          <Dropdown
            label=""
            options={datasources.map((d) => ({ value: d.id, label: d.name }))}
            value={selectedDatasourceId}
            onChange={setSelectedDatasourceId}
            placeholder="Select a datasource"
          />
        </div>
        <div className={styles.toolbarActions}>
          <Button variant="secondary" size="sm" onClick={refresh} disabled={isLoading}>
            <RefreshCw size={14} /> Refresh
          </Button>
          <Button variant="secondary" size="sm" onClick={() => handleExport('csv')} disabled={isExporting || !selectedDatasourceId}>
            <Download size={14} /> {isExporting ? 'Exporting…' : 'Export'}
          </Button>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => fileInputRef.current?.click()}
            disabled={isImporting || !selectedDatasourceId}
          >
            <Upload size={14} /> {isImporting ? 'Uploading…' : 'Upload Answers'}
          </Button>
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv,.json,.xlsx,.xls"
            style={{ display: 'none' }}
            onChange={handleImportFile}
          />
          <Button variant="secondary" size="sm" onClick={handleScore} disabled={isScoring || !selectedDatasourceId}>
            <Gauge size={14} /> {isScoring ? 'Scoring…' : 'Score'}
          </Button>
          <Button variant="secondary" size="sm" onClick={handleAddToRag} disabled={isAddingRag || !selectedDatasourceId}>
            <DatabaseZap size={14} /> {isAddingRag ? 'Adding…' : 'Add to RAG'}
          </Button>
          <Button variant="primary" size="sm" onClick={handleGenerate} disabled={isGenerating || !selectedDatasourceId}>
            <Sparkles size={14} /> {isGenerating ? 'Preparing…' : 'Prepare Questions'}
          </Button>
        </div>
      </div>

      <div className={styles.prepareCard}>
        <div className={styles.stakeholderRow}>
          <Users size={15} className={styles.stakeholderIcon} />
          <input
            className={styles.stakeholderInput}
            placeholder="Stakeholders for question preparation (comma-separated, e.g. Product Owner, Security Lead)"
            value={stakeholders}
            onChange={(e) => setStakeholders(e.target.value)}
          />
        </div>
        <div className={styles.presetChips}>
          <span className={styles.presetLabel}>Prepare for role:</span>
          {STAKEHOLDER_PRESETS.map((role) => (
            <button
              key={role}
              type="button"
              className={`${styles.chip} ${
                selectedStakeholders.includes(role) ? styles.chipActive : ''
              }`}
              onClick={() => toggleStakeholder(role)}
            >
              {selectedStakeholders.includes(role) && <Check size={12} />} {role}
            </button>
          ))}
        </div>
      </div>

      {scoreResult && (
        <div className={styles.scorePanel}>
          <button className={styles.scoreClose} onClick={() => setScoreResult(null)}>
            <X size={14} />
          </button>
          <div className={styles.scorePanelMain}>
            <div className={styles.scoreCircle} data-score={scoreTone(scoreResult.overallScore)}>
              {scoreResult.overallScore}
              <span className={styles.scoreOutOf}>/100</span>
            </div>
            <div className={styles.scoreMetrics}>
              <div className={styles.scoreTitle}>
                <Gauge size={15} /> Document readiness
              </div>
              <div className={styles.metric}>
                <div className={styles.metricLabel}>
                  <span>Completeness</span>
                  <span>
                    {scoreResult.answeredCount}/{scoreResult.totalCount} answered
                  </span>
                </div>
                <div className={styles.bar}>
                  <div
                    className={styles.barFill}
                    data-score="good"
                    style={{
                      width: `${
                        scoreResult.totalCount
                          ? Math.round((100 * scoreResult.answeredCount) / scoreResult.totalCount)
                          : 0
                      }%`,
                    }}
                  />
                </div>
              </div>
              <div className={styles.metric}>
                <div className={styles.metricLabel}>
                  <span>Average answer quality</span>
                  <span>{qualityAvg}/100</span>
                </div>
                <div className={styles.bar}>
                  <div
                    className={styles.barFill}
                    data-score={scoreTone(qualityAvg)}
                    style={{ width: `${qualityAvg}%` }}
                  />
                </div>
              </div>
            </div>
          </div>

          {scoreBreakdown.length > 0 && (
            <div className={styles.scoreBreakdown}>
              <div className={styles.breakdownTitle}>Score by stakeholder</div>
              {scoreBreakdown.map((b) => (
                <div key={b.stakeholder} className={styles.breakdownRow}>
                  <span className={styles.breakdownName}>{b.stakeholder}</span>
                  <div className={styles.bar}>
                    <div
                      className={styles.barFill}
                      data-score={scoreTone(b.avg ?? 0)}
                      style={{ width: `${b.avg ?? 0}%` }}
                    />
                  </div>
                  <span className={styles.breakdownVal}>
                    {b.avg !== null ? `${b.avg}/100` : '—'}
                  </span>
                  <span className={styles.breakdownAnswered}>
                    {b.answered}/{b.total} answered
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {notice && <div className={styles.noticeBanner}>{notice}</div>}
      {error && <div className={styles.errorBanner}>{error}</div>}

      <div className={styles.grid}>
        {/* Questions column */}
        <div className={styles.questionsPanel}>
          <div className={styles.panelHeader}>
            <h3>Questions <span className={styles.count}>{questions.length}</span></h3>
          </div>

          <div className={styles.addRow}>
            <input
              className={styles.addInput}
              placeholder="Add a question manually…"
              value={newQuestionText}
              onChange={(e) => setNewQuestionText(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleAddManual()}
            />
            <Button variant="secondary" size="sm" onClick={handleAddManual} disabled={!newQuestionText.trim()}>
              <Plus size={14} /> Add
            </Button>
          </div>

          {isLoading ? (
            <div className={styles.loading}>Loading…</div>
          ) : questions.length === 0 ? (
            <div className={styles.emptyState}>
              <Sparkles size={28} />
              <p>No questions yet. Click <strong>Prepare Questions</strong> to draft them from the documents.</p>
            </div>
          ) : (
            <div className={styles.groups}>
              {questionGroups.map((group) => {
                const stats = groupStats(group.items);
                return (
                  <div key={group.stakeholder} className={styles.group}>
                    <div className={styles.groupHeader}>
                      <span className={styles.groupTitle}>
                        <Users size={14} /> {group.stakeholder}
                        <span className={styles.count}>{stats.total}</span>
                      </span>
                      <span className={styles.groupMeta}>
                        {stats.answered}/{stats.total} answered
                        {stats.avg !== null && (
                          <span className={styles.groupScore} data-score={scoreTone(stats.avg)}>
                            avg {stats.avg}/100
                          </span>
                        )}
                      </span>
                    </div>
                    <ul className={styles.questionList}>
                      {group.items.map((q) => (
                        <li key={q.id} className={styles.questionItem}>
                  <div className={styles.questionTop}>
                    <span className={`${styles.statusBadge} ${statusClass(q.status)}`}>
                      {STATUS_LABELS[q.status]}
                    </span>
                    <span className={styles.sourceBadge}>{q.source === 'ai' ? 'AI draft' : 'Manual'}</span>
                    {q.stakeholder && (
                      <span className={styles.stakeholderBadge}>
                        <Users size={11} /> {q.stakeholder}
                      </span>
                    )}
                    {typeof q.answerScore === 'number' && (
                      <span
                        className={styles.scoreBadge}
                        data-score={q.answerScore >= 60 ? 'good' : q.answerScore >= 30 ? 'mid' : 'low'}
                        title={q.answerScoreRationale || ''}
                      >
                        <Gauge size={11} /> {q.answerScore}/100
                      </span>
                    )}
                  </div>

                  {editingId === q.id ? (
                    <div className={styles.editRow}>
                      <textarea
                        className={styles.editArea}
                        value={editText}
                        onChange={(e) => setEditText(e.target.value)}
                        rows={2}
                      />
                      <div className={styles.inlineActions}>
                        <Button variant="primary" size="sm" onClick={() => handleSaveEdit(q.id)}>
                          <Check size={14} /> Save
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => setEditingId(null)}>
                          <X size={14} /> Cancel
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <p className={styles.questionText}>{q.text}</p>
                  )}

                  {q.answer && (
                    <div className={styles.answerBlock}>
                      <span className={styles.answerLabel}>Answer</span>
                      <p>{q.answer}</p>
                      {q.answeredByEmail && (
                        <span className={styles.answerMeta}>
                          by {q.answeredByEmail}
                          {q.answeredAt ? ` · ${new Date(q.answeredAt).toLocaleString()}` : ''}
                        </span>
                      )}
                    </div>
                  )}

                  {answeringId === q.id && (
                    <div className={styles.editRow}>
                      <textarea
                        className={styles.editArea}
                        placeholder="Enter the stakeholder answer…"
                        value={answerText}
                        onChange={(e) => setAnswerText(e.target.value)}
                        rows={2}
                      />
                      <div className={styles.inlineActions}>
                        <Button variant="primary" size="sm" onClick={() => handleSaveAnswer(q.id)}>
                          <Send size={14} /> Save Answer
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => setAnsweringId(null)}>
                          <X size={14} /> Cancel
                        </Button>
                      </div>
                    </div>
                  )}

                  {editingId !== q.id && answeringId !== q.id && (
                    <div className={styles.actionRow}>
                      {q.status === 'pending_review' && (
                        <>
                          <button className={styles.approveBtn} onClick={() => handleReview(q.id, 'approved')}>
                            <CheckCircle2 size={14} /> Approve
                          </button>
                          <button className={styles.rejectBtn} onClick={() => handleReview(q.id, 'rejected')}>
                            <XCircle size={14} /> Reject
                          </button>
                        </>
                      )}
                      <button
                        className={styles.linkBtn}
                        onClick={() => {
                          setAnsweringId(q.id);
                          setAnswerText(q.answer || '');
                        }}
                      >
                        <MessageSquare size={14} /> {q.answer ? 'Edit Answer' : 'Answer'}
                      </button>
                      <button
                        className={styles.linkBtn}
                        onClick={() => {
                          setEditingId(q.id);
                          setEditText(q.text);
                        }}
                      >
                        <Pencil size={14} /> Edit
                      </button>
                      <button className={styles.dangerLink} onClick={() => handleDelete(q.id)}>
                        <Trash2 size={14} /> Delete
                      </button>
                    </div>
                  )}
                        </li>
                      ))}
                    </ul>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Audit history column */}
        <div className={styles.auditPanel}>
          <div className={styles.panelHeader}>
            <h3><History size={16} /> Audit History <span className={styles.count}>{audit.length}</span></h3>
          </div>
          {audit.length === 0 ? (
            <div className={styles.emptyState}>
              <History size={28} />
              <p>No audit events yet.</p>
            </div>
          ) : (
            <ul className={styles.timeline}>
              {audit.map((e) => (
                <li key={e.id} className={styles.timelineItem}>
                  <span className={styles.timelineIcon}>{auditIcon(e.action)}</span>
                  <div className={styles.timelineBody}>
                    <div className={styles.timelineTop}>
                      <span className={styles.timelineAction}>{AUDIT_LABELS[e.action]}</span>
                      <span className={styles.timelineTime}>
                        {new Date(e.createdAt).toLocaleString()}
                      </span>
                    </div>
                    <p className={styles.timelineSummary}>{e.summary}</p>
                    {e.actorEmail && <span className={styles.timelineActor}>by {e.actorEmail}</span>}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
};
